import React, { useCallback, useEffect, useState } from "react"

import { Input } from "@coval/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"

import type {
  AudioTurnRangeFilterConfig,
  AudioTurnRangeSpeaker,
} from "@/types/metrics/metadata"

import { AUDIO_TURN_RANGE_MODES, AUDIO_TURN_RANGE_SPEAKERS } from "../types"

interface TurnRangeFilterEditorProps {
  filter: AudioTurnRangeFilterConfig
  onChange: (filter: AudioTurnRangeFilterConfig) => void
}

type TurnRangeMode = AudioTurnRangeFilterConfig["mode"]

export const TurnRangeFilterEditor: React.FC<TurnRangeFilterEditorProps> = ({
  filter,
  onChange,
}) => {
  const [localCount, setLocalCount] = useState(filter.count.toString())

  useEffect(() => {
    setLocalCount(filter.count.toString())
  }, [filter.count])

  const currentSpeaker: AudioTurnRangeSpeaker = filter.speaker ?? "any"

  const handleModeChange = useCallback(
    (mode: string) => {
      onChange({ ...filter, mode: mode as TurnRangeMode })
    },
    [filter, onChange],
  )

  const handleSpeakerChange = useCallback(
    (speaker: string) => {
      onChange({ ...filter, speaker: speaker as AudioTurnRangeSpeaker })
    },
    [filter, onChange],
  )

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalCount(value)
    const parsed = parseInt(value, 10)
    if (!isNaN(parsed) && parsed >= 1) {
      onChange({ ...filter, count: parsed })
    }
  }

  const handleCountBlur = () => {
    const parsed = parseInt(localCount, 10)
    if (isNaN(parsed) || parsed < 1) {
      setLocalCount("1")
      onChange({ ...filter, count: 1 })
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={filter.mode} onValueChange={handleModeChange}>
        <SelectTrigger className="w-24" aria-label="Turn range mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AUDIO_TURN_RANGE_MODES.map((mode) => (
            <SelectItem key={mode.value} value={mode.value}>
              {mode.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        type="number"
        min={1}
        step={1}
        value={localCount}
        onChange={handleCountChange}
        onBlur={handleCountBlur}
        aria-label="Turn count"
        className="w-20"
      />

      <span className="text-sm text-coval-text-muted">turns by</span>

      <Select value={currentSpeaker} onValueChange={handleSpeakerChange}>
        <SelectTrigger className="w-36" aria-label="Turn range speaker">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AUDIO_TURN_RANGE_SPEAKERS.map((speaker) => (
            <SelectItem key={speaker.value} value={speaker.value}>
              {speaker.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
